/**
 * React hook for tracking page metadata event.
 *
 * Features:
 * - Automatically reads pageName and siteSection from `window.utag_data`
 * - Warns in development if required fields are missing
 * - Allows optional overrides via eventData parameter
 */

import React, { useCallback } from 'react';

import { debugCohesionError } from '../../../../utils/debug-logger';
import { useCohesion } from '../../CohesionProvider';
import PageMetadataEvent from './index';

const { createEventPayload } = PageMetadataEvent;

/**
 * @returns {(eventData?: Partial<import('zod').infer<
 *   typeof import('./index').default.ReactComponentInputSchema
 * >>) => void}
 */
export function useTrackPageMetadata() {
  const { trackEvent } = useCohesion();

  return useCallback(
    (eventData = {}) => {
      if (!window.utag_data) {
        return;
      }

      const { PageName = '', SiteSection = '' } = window.utag_data;

      if (process.env.NODE_ENV !== 'production' && !PageName) {
        debugCohesionError(() =>
          console.error(
            '🚨 [Cohesion] Required field `window.utag_data.PageName` not found - cannot reliably track page metadata',
            { utag_data: window.utag_data },
          ),
        );
      }

      trackEvent(
        createEventPayload({
          pageName: PageName,
          siteSection: SiteSection,
          featureFlags: [],
          xBackendName: '',
          internalTrackingCode: '',
          externalTrackingCodes: '',
          campaignProviderKey: '',
          programID: '',

          // allow caller overrides/additions
          ...eventData,
        }),
      );
    },
    [trackEvent],
  );
}
